/**
 * CoreStatusContext — ADR-099 D8: the single poll behind the "Core Updates"
 * surface. Fetched ONCE here (on mount + a light interval) and shared by the
 * AppShell nav badge, the persistent banner, and the Updates page — rather
 * than each of those independently hitting GET /api/core-status.
 *
 * Never throws into a page: a failed fetch (console unreachable, session
 * expired, etc.) just leaves `status` as whatever it last was (or null) and
 * is reflected in `error`/`loading`. The backend itself already fails soft
 * (available:false) for upstream/console failures — this layer additionally
 * tolerates the API CALL failing outright (e.g. a transient network blip
 * hitting this fleet's own MC) without ever crashing a page.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { api, type CoreStatus } from '../api/client'

const POLL_INTERVAL_MS = 60_000

export interface CoreStatusContextValue {
  status: CoreStatus | null
  loading: boolean
  /** True once the FIRST fetch has resolved (success or failure) — lets a
   * consumer distinguish "still loading" from "loaded, nothing to show." */
  loaded: boolean
  /** Re-fetch immediately (e.g. right after dispatching an apply). */
  refresh: () => void
}

const CoreStatusContext = createContext<CoreStatusContextValue | null>(null)

export function CoreStatusProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<CoreStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  // Guards against a stale in-flight response landing after unmount.
  const cancelledRef = useRef(false)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.getCoreStatus()
      if (!cancelledRef.current) setStatus(res)
    } catch {
      // Non-fatal — leave the previous status (or null) in place; the nav
      // badge/banner just reflect whatever was last known (or nothing).
    } finally {
      if (!cancelledRef.current) {
        setLoading(false)
        setLoaded(true)
      }
    }
  }, [])

  useEffect(() => {
    cancelledRef.current = false
    void fetchStatus()
    const interval = setInterval(() => { void fetchStatus() }, POLL_INTERVAL_MS)
    return () => {
      cancelledRef.current = true
      clearInterval(interval)
    }
  }, [fetchStatus])

  return (
    <CoreStatusContext.Provider value={{ status, loading, loaded, refresh: () => { void fetchStatus() } }}>
      {children}
    </CoreStatusContext.Provider>
  )
}

/** useCoreStatus — must be used inside a CoreStatusProvider. Throws if missing. */
export function useCoreStatus(): CoreStatusContextValue {
  const ctx = useContext(CoreStatusContext)
  if (!ctx) throw new Error('useCoreStatus must be used within CoreStatusProvider')
  return ctx
}
